import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  HeartHandshake, Sparkles, Target, Users, Trophy, ChevronLeft, ArrowLeft,
  Waves, Music, Brush, Activity, Home, Briefcase, BookOpen, Phone, Mail, MapPin,
  Award, ShieldCheck, MessageCircle, ClipboardCheck, TrendingUp, GraduationCap, UserCheck, PlayCircle, Play, X,
  Palette, Puzzle, Bus, Share2, Link as LinkIcon
} from "lucide-react";

const ICON_MAP = { Activity, BookOpen, Brush, Home, Music, Waves, Users, Trophy, Target, HeartHandshake, Sparkles, GraduationCap, Briefcase, ClipboardCheck, Palette, Puzzle, Bus, MessageCircle, Award };

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { icon: Activity, title: "الأنشطة الرياضية والحركية", desc: "أنشطة اللياقة داخل الجيم الرياضي المخصّص + جلسات سباحة علاجية لدعم الاتزان والتنسيق الحسي.", color: "from-sky-400 to-blue-500" },
  { icon: Home, title: "مهارات الحياة اليومية (ADL)", desc: "تنمية الاستقلالية في الطعام والنظافة واللبس والذهاب للحمام.", color: "from-violet-400 to-purple-500" },
  { icon: Briefcase, title: "التأهيل المهني", desc: "أنشطة وظيفية تُهيّئ للاندماج المهني حسب قدرات كل فرد.", color: "from-amber-400 to-orange-500" },
  { icon: Brush, title: "الفن والترفيه", desc: "رسم، تشكيل، موسيقى، ورحلات ميدانية منظمة.", color: "from-fuchsia-400 to-pink-500" },
  { icon: BookOpen, title: "التعليم الوظيفي", desc: "برامج قراءة، أرقام، ألوان، أشكال ملائمة لكل مستوى.", color: "from-emerald-400 to-green-500" },
  { icon: ClipboardCheck, title: "المتابعة والتقييم", desc: "تقييمات دورية موثقة تُشارك مع الأسرة بشفافية.", color: "from-indigo-400 to-blue-500" },
];

// Illustrative imagery for the hero collage (falls back to gradient + icon if the image fails to load)
const HERO_COLLAGE = [
  { label: "الأنشطة الرياضية", icon: Activity, img: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=600&h=600&fit=crop&auto=format", bg: "linear-gradient(135deg, #38bdf8, #1d4ed8)" },
  { label: "التعليم والقراءة", icon: BookOpen, img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&h=600&fit=crop&auto=format", bg: "linear-gradient(135deg, #34d399, #059669)" },
  { label: "الفن والإبداع", icon: Brush, img: "https://images.unsplash.com/photo-1587691592099-24045742c181?w=600&h=600&fit=crop&auto=format", bg: "linear-gradient(135deg, #f472b6, #db2777)" },
  { label: "مهارات يومية", icon: Home, img: "https://images.unsplash.com/photo-1607113256158-56a934936ef1?w=600&h=600&fit=crop&auto=format", bg: "linear-gradient(135deg, #fbbf24, #ea580c)" },
];

function collageImageFor(s, i) {
  const paths = s.hero_collage_paths || [];
  if (paths[i]) return `${BACKEND}/api/public/landing-collage/${i}`;
  return HERO_COLLAGE[i].img;
}

const DEFAULTS = {
  site_title: "kuwait skills.com",
  center_name: "شركة كويت سكيلز للاستشارات - Happy Day Center",
  hero_badge: "نموذج رائد في التأهيل المتكامل",
  hero_title_1: "كل طفل قصة نجاح",
  hero_title_2: "تبدأ بخطوة صغيرة.",
  hero_description: "مركز Happy Day هو مركز متخصص في تقديم البرامج التأهيلية والتعليمية لذوي الاحتياجات الخاصة.",
  hero_cta_text: "بوابة أولياء الأمور",
  vision_title: "🌟 رؤية المركز",
  vision_body: "",
  mission_title: "🎯 رسالة المركز",
  mission_body: "",
  objectives_title: "🧠 أهداف المركز",
  objectives: [],
  cta_title: "هل أنت ولي أمر؟",
  cta_body: "",
  cta_button: "دخول البوابة",
  primary_color: "#0284c7",
  secondary_color: "#2563eb",
  accent_color: "#7c3aed",
  contact_phone: "تواصل معنا",
  contact_email: "info@happyday.center",
  hero_image_url: "",
  director_name: "الأستاذ محمد عاطف",
  director_title: "مدير مركز Happy Day",
  director_bio: "بخبرة تتجاوز 15 عاماً في التربية الخاصة ومعتمَد كفني سلوك مسجّل (RBT®)، يمزج الأستاذ محمد عاطف بين إتقان تحليل السلوك التطبيقي (ABA) وإعداد الخطط التربوية الفردية (IEP) في التعامل مع طيف التوحد وصعوبات التعلم والإعاقات الذهنية.",
  director_photo_url: "/director.jpg",
  hero_collage_labels: ["الأنشطة الرياضية", "التعليم والقراءة", "الفن والإبداع", "مهارات يومية"],
};

export default function LandingPage() {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [teamFilter, setTeamFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [directorVideoOpen, setDirectorVideoOpen] = useState(false);
  const [chosenPoster, setChosenPoster] = useState(null); // { slot: 'a'|'b'|null }
  const [posterPlayLogged, setPosterPlayLogged] = useState(false);
  const [certProfile, setCertProfile] = useState(null); // { name, mastered_domains[], cert_domains[], ... }
  const [linkCopied, setLinkCopied] = useState(false);
  const [contribsOpen, setContribsOpen] = useState(false);
  const [contribs, setContribs] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/overview`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => setData({ stats: {}, articles: [], announcements: [], settings: DEFAULTS }));
    axios.get(`${BACKEND}/api/public/team`, { withCredentials: false })
      .then((r) => setTeam(r.data || []))
      .catch(() => setTeam([]));
    axios.get(`${BACKEND}/api/public/director-poster-choose`, { withCredentials: false })
      .then((r) => setChosenPoster(r.data || null))
      .catch(() => setChosenPoster(null));
    // Certificate QR anchor: /welcome#cert-{child_id}
    const hash = (window.location.hash || "").replace(/^#/, "");
    const m = hash.match(/^cert-([a-f0-9]{24})$/i);
    if (m) {
      const cid = m[1];
      axios.get(`${BACKEND}/api/public/child-profile/${cid}`, { withCredentials: false })
        .then((r) => setCertProfile(r.data))
        .catch(() => setCertProfile(null));
      // Log the scan asynchronously (fire-and-forget)
      axios.post(`${BACKEND}/api/public/cert-scan/${cid}`, {}, { withCredentials: false }).catch(() => {});
    }
  }, []);

  const openDirectorVideo = () => {
    setPosterPlayLogged(false);
    setDirectorVideoOpen(true);
  };

  const logPosterPlay = () => {
    if (posterPlayLogged) return;
    if (!chosenPoster?.slot) return;
    setPosterPlayLogged(true);
    axios.post(`${BACKEND}/api/public/director-poster-play`, { slot: chosenPoster.slot }, { withCredentials: false }).catch(() => {});
  };

  const s = useMemo(() => ({ ...DEFAULTS, ...(data?.settings || {}) }), [data]);
  useEffect(() => {
    if (s.site_title) document.title = s.site_title;
  }, [s.site_title]);
  const stats = data?.stats || {};

  const cssVars = {
    "--brand-primary": s.primary_color,
    "--brand-secondary": s.secondary_color,
    "--brand-accent": s.accent_color,
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page" style={cssVars}>
      {/* Certificate QR Celebration Modal */}
      {certProfile && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cert-celebration-modal" onClick={() => setCertProfile(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl border-4 border-amber-400 animate-in fade-in zoom-in-95 duration-300">
            <button onClick={() => setCertProfile(null)} className="absolute top-4 left-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="cert-celebration-close">
              <X className="w-4 h-4" />
            </button>
            <div className="text-center">
              <div className="text-6xl mb-2 animate-bounce">🏆</div>
              <div className="inline-block px-4 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-black mb-3">شهادة إنجاز موثّقة</div>
              {certProfile.photo_url && (
                <img src={certProfile.photo_url} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mb-3 ring-4 ring-amber-300 shadow-lg" />
              )}
              <div className="text-3xl md:text-4xl font-black text-slate-900" data-testid="cert-celebration-name">{certProfile.name}</div>
              <div className="text-sm text-slate-500 mt-1">نبارك التقدّم والإنجاز في مركز Happy Day</div>
              <div className="grid grid-cols-2 gap-3 mt-6 text-center">
                <div className="rounded-2xl bg-gradient-to-b from-amber-50 to-white border-2 border-amber-200 p-4">
                  <div className="text-3xl font-black text-amber-700">{certProfile.certificates_count}</div>
                  <div className="text-[11px] text-amber-800 font-bold mt-1">شهادات إنجاز</div>
                </div>
                <div className="rounded-2xl bg-gradient-to-b from-emerald-50 to-white border-2 border-emerald-200 p-4">
                  <div className="text-3xl font-black text-emerald-700">{certProfile.mastered_count}</div>
                  <div className="text-[11px] text-emerald-800 font-bold mt-1">مهارات متقنة مدى الحياة</div>
                </div>
              </div>
              {certProfile.cert_domains?.length > 0 && (
                <div className="mt-5 text-right">
                  <div className="text-xs font-black text-slate-700 mb-2">🎓 المجالات المُنجَزة:</div>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    {certProfile.cert_domains.map((d) => (
                      <span key={d.domain_id} className="inline-block px-3 py-1 rounded-full text-white text-[11px] font-black" style={{ background: d.color }} data-testid={`cert-celebration-domain-${d.domain_id}`}>
                        {d.name} ({d.percent}%)
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share row */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const domains = (certProfile.cert_domains || []).map((d) => `• ${d.name} (${d.percent}%)`).join("\n");
                    const url = window.location.href;
                    const text = `🏆 نبارك لـ ${certProfile.name} الحصول على ${certProfile.certificates_count} شهادة إنجاز في مركز Happy Day${domains ? "\n\nالمجالات المُنجَزة:\n" + domains : ""}\n\nشاهد صفحة الإنجاز:\n${url}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-l from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-black text-sm shadow-md hover:shadow-lg transition-all"
                  data-testid="cert-share-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> شارك عبر واتساب
                </button>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2500);
                    } catch {
                      // Fallback: select via a hidden textarea
                      const ta = document.createElement("textarea");
                      ta.value = window.location.href;
                      document.body.appendChild(ta); ta.select();
                      try { document.execCommand("copy"); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); } catch { /* noop */ }
                      ta.remove();
                    }
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white border-2 border-slate-200 hover:border-sky-400 hover:bg-sky-50 text-slate-800 font-black text-sm transition-all"
                  data-testid="cert-share-copy-link"
                >
                  <LinkIcon className="w-4 h-4" /> {linkCopied ? "✓ تم النسخ" : "نسخ الرابط"}
                </button>
                {typeof navigator !== "undefined" && "share" in navigator && (
                  <button
                    onClick={() => {
                      navigator.share({
                        title: `شهادة إنجاز — ${certProfile.name}`,
                        text: `🏆 نبارك لـ ${certProfile.name} الحصول على ${certProfile.certificates_count} شهادة في مركز Happy Day`,
                        url: window.location.href,
                      }).catch(() => {});
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white border-2 border-slate-200 hover:border-sky-400 hover:bg-sky-50 text-slate-800 font-black text-sm transition-all"
                    data-testid="cert-share-native"
                  >
                    <Share2 className="w-4 h-4" /> مشاركة أخرى
                  </button>
                )}
              </div>

              <div className="mt-4 text-[11px] text-slate-400">شارك الفرحة مع العائلة والأصدقاء ❤️</div>
            </div>
          </div>
        </div>
      )}

      {/* Contributions Modal */}
      {contribsOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setContribsOpen(false)} data-testid="contribs-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl border-4 border-violet-400">
            <div className="p-6 pb-4 border-b bg-gradient-to-l from-violet-600 to-fuchsia-600 text-white flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-black uppercase tracking-widest opacity-90">إسهامات خارجية</div>
                <div className="text-2xl font-black mt-1">دوراتي التدريبية وورش العمل</div>
              </div>
              <button onClick={() => setContribsOpen(false)} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="contribs-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-90px)]">
              {contribs.length === 0 ? (
                <div className="text-center py-14">
                  <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <div className="text-slate-500 font-bold">لم تُضَف إسهامات بعد</div>
                  <div className="text-xs text-slate-400 mt-1">سيتم رفع الدورات وورش العمل هنا قريباً</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contribs.map((c) => {
                    const media = c.media || [];
                    let cover = null;
                    if (c.cover_media_id) cover = media.find((m) => m.id === c.cover_media_id);
                    if (!cover) cover = media.find((m) => m.type === "video") || media[0];
                    const kindLabel = c.kind === "seminar" ? "🎓 ندوة" : c.kind === "workshop" ? "🛠️ ورشة" : c.kind === "lecture" ? "🎤 محاضرة" : c.kind === "talk" ? "🗣️ حديث" : "📚 دورة";
                    return (
                      <div key={c.id} className="rounded-2xl border-2 border-violet-100 overflow-hidden bg-gradient-to-br from-violet-50/40 to-white hover:border-violet-300 hover:shadow-lg transition-all" data-testid={`contrib-${c.id}`}>
                        {cover && (
                          <button
                            onClick={() => window.open(`/welcome/contributions#${c.id}`, "_self")}
                            className="block w-full aspect-video bg-slate-900 relative overflow-hidden group"
                            data-testid={`contrib-cover-${c.id}`}
                          >
                            {cover.type === "video" ? (
                              <>
                                <video
                                  src={`${BACKEND}/api/public/contribution-media/${c.id}/${cover.id}`}
                                  poster={cover.poster_path ? `${BACKEND}/api/public/contribution-media/${c.id}/${cover.id}/poster` : undefined}
                                  muted
                                  loop
                                  autoPlay
                                  playsInline
                                  preload="auto"
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-center justify-center group-hover:from-black/80 transition">
                                  <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="absolute w-24 h-24 rounded-full bg-white/30 animate-ping" />
                                    <span className="absolute w-20 h-20 rounded-full bg-white/20" />
                                    <span className="relative w-20 h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                      <Play className="w-10 h-10 text-violet-700 fill-violet-700 mr-[-4px]" />
                                    </span>
                                  </span>
                                  <span className="absolute bottom-3 right-3 bg-red-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> فيديو
                                  </span>
                                </div>
                              </>
                            ) : (
                              <img src={`${BACKEND}/api/public/contribution-media/${c.id}/${cover.id}`} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            )}
                            {media.length > 1 && (
                              <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur text-white text-[11px] font-black px-2.5 py-1 rounded-full flex items-center gap-1">
                                🖼️ {media.length} صورة
                              </div>
                            )}
                          </button>
                        )}
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="inline-block px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[10px] font-black">
                              {kindLabel}
                            </div>
                            {c.year && <div className="text-[10px] font-black text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{c.year}</div>}
                          </div>
                          <div className="font-black text-slate-900 mt-1">{c.title}</div>
                          {c.event_date && (
                            <div className="text-[11px] text-emerald-700 font-black mt-1.5">
                              📅 {new Date(c.event_date).toLocaleDateString("ar-EG", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                            </div>
                          )}
                          {(c.org || c.location) && (
                            <div className="text-xs text-slate-500 mt-1 font-bold">
                              {c.org}{c.org && c.location ? " · " : ""}{c.location}
                            </div>
                          )}
                          {c.description && <div className="text-xs text-slate-700 mt-2 leading-relaxed line-clamp-3 whitespace-pre-line">{c.description}</div>}
                          <div className="mt-3 flex items-center gap-2">
                            <Link
                              to={`/welcome/contributions#${c.id}`}
                              onClick={() => setContribsOpen(false)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-l from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-black text-xs shadow"
                              data-testid={`contrib-open-full-${c.id}`}
                            >
                              عرض التفاصيل والألبوم كاملاً <ArrowLeft className="w-3.5 h-3.5" />
                            </Link>
                            {c.url && (
                              <a href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border-2 border-violet-200 text-xs font-black text-violet-700 hover:bg-violet-50" data-testid={`contrib-link-${c.id}`}>
                                🔗 خارجي
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={s.center_name} className="w-12 h-12 rounded-2xl shadow-sm bg-white p-0.5" />
            <div>
              <div className="text-base sm:text-xl font-black text-slate-900 leading-tight" data-testid="landing-center-name">{s.center_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#services" className="hidden md:inline text-sm font-bold text-slate-600 hover:text-slate-900">الخدمات</a>
            <a href="#about" className="hidden md:inline text-sm font-bold text-slate-600 hover:text-slate-900">عن المركز</a>
            <a href="#articles" className="hidden md:inline text-sm font-bold text-slate-600 hover:text-slate-900">مقالات</a>
            <Link to="/login">
              <Button
                className="h-11 gap-2 text-white shadow-lg"
                style={{ background: `linear-gradient(to left, ${s.primary_color}, ${s.secondary_color})` }}
                data-testid="landing-login-btn"
              >
                تسجيل الدخول <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.secondary_color})` }} />
          <div className="absolute top-20 -left-40 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: `linear-gradient(135deg, ${s.accent_color}, ${s.primary_color})` }} />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 rounded-full opacity-25 blur-3xl" style={{ background: `linear-gradient(135deg, ${s.secondary_color}, ${s.accent_color})` }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border rounded-full px-4 py-1.5 text-sm font-bold shadow-sm"
              style={{ borderColor: s.primary_color + "44", color: s.primary_color }}
            >
              <Sparkles className="w-4 h-4" /> {s.hero_badge}
            </div>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 leading-tight">
              {s.hero_title_1}
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(to left, ${s.primary_color}, ${s.secondary_color}, ${s.accent_color})` }}
              >
                {s.hero_title_2}
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl whitespace-pre-line">
              {s.hero_description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button
                  size="lg"
                  className="h-14 px-8 gap-2 text-base text-white shadow-xl"
                  style={{ background: `linear-gradient(to left, ${s.primary_color}, ${s.secondary_color})` }}
                  data-testid="hero-cta"
                >
                  <HeartHandshake className="w-5 h-5" /> {s.hero_cta_text}
                </Button>
              </Link>
              <a href="#services">
                <Button size="lg" variant="outline" className="h-14 px-8 gap-2 text-base border-2">
                  استكشف خدماتنا <ChevronLeft className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="relative bg-white rounded-3xl shadow-2xl border border-white p-6 rotate-1 hover:rotate-0 transition-transform">
              {s.hero_media_path && s.hero_media_type === "video" ? (
                <video
                  src={`${BACKEND}/api/public/landing-hero`}
                  autoPlay muted loop playsInline
                  className="w-full max-w-xs mx-auto rounded-2xl aspect-square object-cover"
                />
              ) : s.hero_media_path && s.hero_media_type === "image" ? (
                <img src={`${BACKEND}/api/public/landing-hero`} alt="Happy Day" className="w-full max-w-xs mx-auto rounded-2xl aspect-square object-cover" />
              ) : (
                <img src={s.hero_image_url || "/logo.png"} alt="Happy Day" className="w-full max-w-xs mx-auto rounded-2xl" />
              )}
              {s.director_video_path && (
                <button
                  onClick={openDirectorVideo}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl font-black text-white shadow-lg transition-all bg-gradient-to-l from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  data-testid="director-welcome-cta"
                >
                  <PlayCircle className="w-5 h-5" /> شاهد رسالة الترحيب من المدير
                </button>
              )}
              {/* Service imagery collage (replaces stats grid) */}
              <div className="mt-4 grid grid-cols-2 gap-2" data-testid="hero-collage">
                {HERO_COLLAGE.map((it, i) => {
                  const iconName = (s.hero_collage_icons && s.hero_collage_icons[i]) || "";
                  const Icon = ICON_MAP[iconName] || it.icon;
                  const label = (s.hero_collage_labels && s.hero_collage_labels[i]) || it.label;
                  return (
                    <div
                      key={i}
                      className={`relative aspect-square rounded-2xl overflow-hidden shadow-sm group`}
                      style={{ background: it.bg }}
                    >
                      <img
                        src={collageImageFor(s, i)}
                        alt={label}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className={`absolute inset-x-0 bottom-0 p-2 text-center bg-gradient-to-t from-black/70 to-transparent`}>
                        <div className="flex items-center justify-center gap-1 text-white text-[11px] font-black">
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </div>
                      </div>
                      <Icon className={`absolute top-2 right-2 w-5 h-5 text-white/80 drop-shadow`} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="absolute -top-6 -left-6 w-20 h-20 rounded-3xl rotate-12 shadow-lg" style={{ background: `linear-gradient(135deg, ${s.accent_color}, ${s.secondary_color})` }} />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full shadow-lg" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.accent_color})` }} />
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section id="about" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid md:grid-cols-2 gap-6">
          <BigCard icon={Trophy} title={s.vision_title} body={s.vision_body} color={s.primary_color} color2={s.secondary_color} />
          <BigCard icon={Target} title={s.mission_title} body={s.mission_body} color="#059669" color2="#0d9488" />
        </div>

        {(s.objectives?.length > 0) && (
          <div className="mt-8 bg-gradient-to-br from-violet-50 via-white to-sky-50 border-2 border-violet-100 rounded-3xl p-8 lg:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl text-white flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${s.accent_color}, ${s.secondary_color})` }}>
                <Award className="w-6 h-6" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900">{s.objectives_title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {s.objectives.map((obj, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 backdrop-blur rounded-2xl p-4 border border-violet-100">
                  <div className="w-8 h-8 rounded-full text-white font-black flex items-center justify-center shrink-0 text-sm" style={{ background: `linear-gradient(135deg, ${s.accent_color}, ${s.secondary_color})` }}>
                    {i + 1}
                  </div>
                  <span className="text-slate-700 font-semibold leading-relaxed">{obj}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Director */}
      {(s.director_name || s.director_bio) && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8 lg:pb-16">
          <div className="relative overflow-hidden rounded-3xl border-2 border-slate-100 bg-white shadow-xl">
            <div className="absolute inset-y-0 right-0 w-1/3 opacity-[0.08]" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.accent_color})` }} />
            <div className="relative grid md:grid-cols-[280px_1fr] gap-8 p-8 lg:p-12 items-center">
              <div className="relative mx-auto md:mx-0">
                <div className="absolute -inset-3 rounded-3xl blur-2xl opacity-30" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.accent_color})` }} />
                <div className="relative w-56 h-56 lg:w-64 lg:h-64 rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white">
                  <img
                    src={s.director_photo_url || "/director.jpg"}
                    alt={s.director_name}
                    className="w-full h-full object-cover"
                    data-testid="director-photo"
                  />
                </div>
                <div className="absolute -bottom-3 -left-3 rounded-2xl px-4 py-2 text-white text-xs font-black shadow-lg inline-flex items-center gap-1.5" style={{ background: `linear-gradient(to left, ${s.primary_color}, ${s.secondary_color})` }}>
                  <UserCheck className="w-4 h-4" /> RBT® معتمد
                </div>
              </div>

              <div>
                <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold shadow-sm border" style={{ borderColor: s.primary_color + "44", color: s.primary_color, background: s.primary_color + "0a" }}>
                  <GraduationCap className="w-4 h-4" /> {s.director_title}
                </div>
                <h2 className="mt-3 text-3xl md:text-4xl font-black text-slate-900" data-testid="director-name">{s.director_name}</h2>
                <p className="mt-3 text-slate-600 text-base font-bold" data-testid="director-tagline">
                  متخصص تربوي · مدرب معتمد · خبرة تتجاوز 15 عاماً في التربية الخاصة
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["ماجستير التربية الخاصة", "تحليل السلوك التطبيقي (ABA)", "الخطط التربوية الفردية (IEP)", "طيف التوحد", "صعوبات التعلم"].map((tag) => (
                    <span key={tag} className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{tag}</span>
                  ))}
                </div>

                {/* External Contributions placeholder — future uploads: training courses & workshops given outside the center */}
                <a
                  href="#external-contributions"
                  onClick={(e) => {
                    e.preventDefault();
                    setContribsOpen(true);
                    axios.get(`${BACKEND}/api/public/director-contributions`).then((r) => setContribs(r.data || [])).catch(() => setContribs([]));
                  }}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-l from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700 text-white font-black text-sm shadow-lg hover:shadow-xl transition-all"
                  data-testid="director-external-contributions"
                  title="الدورات التدريبية وورش العمل التي أُعطيت خارج المركز"
                >
                  <Trophy className="w-4 h-4" /> الإسهامات الخارجية
                </a>

                {/* Certifications gallery */}
                {(s.certifications || []).length > 0 && (
                  <div className="mt-6">
                    <div className="text-xs font-black text-slate-500 mb-2 uppercase tracking-wide">شهادات وأوسمة</div>
                    {(() => {
                      const CAT_META = {
                        master: { label: "ماجستير", color: "#7c3aed", bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-700" },
                        diploma: { label: "دبلومات", color: "#0284c7", bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-700" },
                        professional: { label: "شهادات مهنية", color: "#059669", bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700" },
                      };
                      const groups = {};
                      (s.certifications || []).forEach((c) => {
                        const k = c.category || "professional";
                        (groups[k] = groups[k] || []).push(c);
                      });
                      // Within each category: featured first (preserving order), then rest
                      Object.keys(groups).forEach((k) => {
                        const arr = groups[k];
                        const featured = arr.filter((c) => c.featured);
                        const rest = arr.filter((c) => !c.featured);
                        groups[k] = [...featured, ...rest];
                      });
                      const order = ["master", "diploma", "professional"];
                      return (
                        <div className="space-y-4" data-testid="director-certs">
                          {order.filter((k) => groups[k]?.length).map((k) => {
                            const meta = CAT_META[k];
                            return (
                              <div key={k}>
                                <div className={`inline-flex items-center gap-1.5 mb-2 px-3 py-1 rounded-full text-xs font-black ${meta.bg} ${meta.text} border ${meta.border}`} data-testid={`cert-cat-${k}`}>
                                  <Award className="w-3 h-3" /> {meta.label}
                                  <span className="opacity-70">({groups[k].length})</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {groups[k].map((c) => (
                                      <button
                                      key={c.id}
                                      onClick={() => setLightbox(c)}
                                      className={`group relative w-20 h-24 rounded-xl overflow-hidden border-2 shadow-sm hover:shadow-lg transition-all ${c.featured ? "ring-2 ring-amber-400 border-amber-400" : meta.border}`}
                                      title={c.title}
                                      data-testid={`cert-thumb-${c.id}`}
                                    >
                                      {c.mime && c.mime.startsWith("image/") ? (
                                        <img src={`${BACKEND}/api/public/landing-cert/${c.id}`} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                      ) : (
                                        <div className={`w-full h-full flex flex-col items-center justify-center ${meta.bg} ${meta.text}`}>
                                          <BookOpen className="w-6 h-6" />
                                          <span className="text-[9px] font-bold mt-1">PDF</span>
                                        </div>
                                      )}
                                      {c.featured && (
                                        <div className="absolute top-1 left-1 rounded-full bg-amber-500 text-white p-1 shadow" data-testid={`cert-star-badge-${c.id}`}>
                                          <Award className="w-3 h-3" />
                                        </div>
                                      )}
                                      {c.issue_year && (
                                        <div className="absolute top-1 right-1 rounded-md px-1.5 py-0.5 bg-white/90 backdrop-blur text-[9px] font-black text-slate-700 shadow" data-testid={`cert-year-badge-${c.id}`}>
                                          {c.issue_year}
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent flex items-end p-1">
                                        <span className="text-white text-[9px] font-bold line-clamp-2 leading-tight">{c.title}</span>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* WhatsApp CTA */}
                {s.director_whatsapp && (
                  <a
                    href={`https://wa.me/${s.director_whatsapp.replace(/[^\d]/g, "")}?text=${encodeURIComponent(s.director_whatsapp_message || "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-2xl font-black text-white shadow-lg hover:shadow-xl transition-all bg-gradient-to-l from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                    data-testid="director-whatsapp-cta"
                  >
                    <MessageCircle className="w-5 h-5" /> احجز موعداً مع المدير عبر واتساب
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Team */}
      {team.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-24">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-white border rounded-full px-4 py-1.5 text-sm font-bold shadow-sm" style={{ borderColor: s.accent_color + "44", color: s.accent_color }}>
              <Users className="w-4 h-4" /> الفريق التخصصي
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900">تعرَّف على المعلمين والمعالجين</h2>
            <p className="mt-3 text-slate-500 max-w-2xl mx-auto">فريق متكامل من المتخصصين يرافق طفلك في رحلة التطور والنمو.</p>
          </div>

          {(() => {
            const specialties = Array.from(new Set(team.map((m) => (m.specialty || "").trim()).filter(Boolean)));
            const filtered = teamFilter === "all" ? team : team.filter((m) => (m.specialty || "").trim() === teamFilter);
            return (
              <>
                {specialties.length > 1 && (
                  <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="team-filter">
                    <button
                      onClick={() => setTeamFilter("all")}
                      className={`px-4 py-2 rounded-full text-xs font-black transition-all ${teamFilter === "all" ? "bg-slate-900 text-white shadow-md" : "bg-white border border-slate-200 text-slate-700"}`}
                      data-testid="team-filter-all"
                    >
                      الكل <span className="opacity-70">({team.length})</span>
                    </button>
                    {specialties.map((sp) => {
                      const count = team.filter((m) => (m.specialty || "").trim() === sp).length;
                      return (
                        <button
                          key={sp}
                          onClick={() => setTeamFilter(sp)}
                          className={`px-4 py-2 rounded-full text-xs font-black transition-all ${teamFilter === sp ? "text-white shadow-md" : "bg-white border border-slate-200 text-slate-700"}`}
                          style={teamFilter === sp ? { background: `linear-gradient(to left, ${s.primary_color}, ${s.accent_color})` } : {}}
                          data-testid={`team-filter-${sp}`}
                        >
                          {sp} <span className="opacity-80">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {filtered.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 font-bold">لا يوجد أعضاء ضمن هذا التخصص</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="team-grid">
                    {filtered.map((m) => (
                      <div key={m.id} className="group text-center bg-white rounded-3xl border border-slate-100 hover:shadow-2xl transition-all hover:-translate-y-1 p-6" data-testid={`team-card-${m.id}`}>
                        <div className="relative mx-auto w-28 h-28 rounded-3xl overflow-hidden ring-4 ring-white shadow-lg mb-4" style={{ background: `linear-gradient(135deg, ${s.primary_color}22, ${s.accent_color}22)` }}>
                          {m.photo_url ? (
                            <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-3xl font-black" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.accent_color})` }}>
                              {(m.name || "?").trim()[0]}
                            </div>
                          )}
                        </div>
                        <h3 className="font-black text-slate-900 text-base leading-tight line-clamp-2">{m.name}</h3>
                        {m.specialty && (
                          <div className="mt-2 inline-block text-xs font-bold px-3 py-1 rounded-full" style={{ background: s.primary_color + "12", color: s.primary_color }}>
                            {m.specialty}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </section>
      )}

      {/* Cert lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="cert-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-xl"
            data-testid="cert-lightbox-close"
          >
            ✕
          </button>
          <div className="relative max-w-3xl w-full max-h-[85vh] bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {lightbox.mime && lightbox.mime.startsWith("image/") ? (
              <img src={`${BACKEND}/api/public/landing-cert/${lightbox.id}`} alt={lightbox.title} className="w-full max-h-[75vh] object-contain bg-slate-50" />
            ) : (
              <iframe src={`${BACKEND}/api/public/landing-cert/${lightbox.id}`} title={lightbox.title} className="w-full h-[75vh] bg-slate-50" />
            )}
            <div className="p-4 bg-white border-t border-slate-100">
              <div className="font-black text-slate-900">{lightbox.title}</div>
            </div>
          </div>
        </div>
      )}

      {/* Director welcome video modal */}
      {directorVideoOpen && (
        <div
          onClick={() => setDirectorVideoOpen(false)}
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="director-video-modal"
        >
          <button
            onClick={() => setDirectorVideoOpen(false)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-xl"
            data-testid="director-video-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative max-w-4xl w-full bg-black rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <video
              src={`${BACKEND}/api/public/director-video`}
              controls
              autoPlay
              poster={chosenPoster?.slot ? `${BACKEND}/api/public/director-poster-variant/${chosenPoster.slot}` : (s.director_video_poster_path ? `${BACKEND}/api/public/director-video-poster` : undefined)}
              onPlay={logPosterPlay}
              className="w-full aspect-video"
              data-testid="director-video-player"
              data-poster-slot={chosenPoster?.slot || ""}
            />
            <div className="p-4 bg-white">
              <div className="font-black text-slate-900 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" /> رسالة ترحيب من {s.director_name}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Services */}
      <section id="services" className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white border rounded-full px-4 py-1.5 text-sm font-bold shadow-sm" style={{ borderColor: s.primary_color + "44", color: s.primary_color }}>
              <Sparkles className="w-4 h-4" /> خدماتنا
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900">برامج تأهيلية وتعليمية متكاملة</h2>
            <p className="mt-3 text-slate-500 max-w-2xl mx-auto">لكل فرد قدرات فريدة يمكن تنميتها من خلال التدريب المنهجي والأنشطة الهادفة.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((sv, i) => (
              <div key={i} className="group relative bg-white rounded-3xl p-6 border border-slate-100 hover:shadow-2xl transition-all hover:-translate-y-1 overflow-hidden">
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${sv.color}`} />
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${sv.color} text-white flex items-center justify-center shadow-lg`}>
                  <sv.icon className="w-7 h-7" />
                </div>
                <h3 className="mt-4 font-black text-lg text-slate-900">{sv.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{sv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section id="articles" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-1.5 text-sm font-bold text-emerald-700 shadow-sm">
            <BookOpen className="w-4 h-4" /> مقالات ومعرفة
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900">مقالات علمية للأسر</h2>
          <p className="mt-3 text-slate-500 max-w-2xl mx-auto">محتوى تربوي وتخصصي يحدّثه فريقنا بانتظام لدعم مسيرة طفلك.</p>
        </div>

        {(!data || data.articles?.length === 0) ? (
          <div className="text-center py-16 text-slate-400 bg-slate-50 rounded-3xl border border-slate-100">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div className="font-bold">قريباً — سيتم نشر المقالات هنا</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.articles.slice(0, 6).map((a) => (
              <article key={a.id} className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-2xl transition-all">
                {a.cover_url ? (
                  <div className="h-44 overflow-hidden bg-slate-100">
                    <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                ) : (
                  <div className="h-44 bg-gradient-to-br from-sky-100 via-emerald-100 to-amber-100 flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-slate-400 opacity-50" />
                  </div>
                )}
                <div className="p-6">
                  {a.category && <span className="text-xs font-black uppercase tracking-wider" style={{ color: s.primary_color }}>{a.category}</span>}
                  <h3 className="mt-2 font-black text-lg text-slate-900 leading-tight line-clamp-2">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.body}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                    <span>{a.author_name}</span>
                    <span>{(a.published_at || a.created_at || "").slice(0, 10)}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-24">
        <div className="relative overflow-hidden rounded-3xl p-8 lg:p-16 text-white" style={{ background: `linear-gradient(135deg, ${s.primary_color}, ${s.secondary_color}, ${s.accent_color})` }}>
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black leading-tight">{s.cta_title}</h2>
              <p className="mt-3 text-white/85 text-lg max-w-xl whitespace-pre-line">{s.cta_body}</p>
            </div>
            <Link to="/login">
              <Button size="lg" className="h-14 px-8 text-base bg-white hover:bg-white/90 shadow-2xl font-black gap-2" style={{ color: s.primary_color }} data-testid="cta-login-btn">
                <ShieldCheck className="w-5 h-5" /> {s.cta_button}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Login Boxes removed per user request */}

      {/* Footer */}
      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Happy Day" className="w-10 h-10 rounded-xl" />
            <div className="text-sm text-slate-500" data-testid="landing-footer-center-name">© {new Date().getFullYear()} {s.center_name} · جميع الحقوق محفوظة</div>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><Phone className="w-4 h-4" /> {s.contact_phone}</span>
            <span className="inline-flex items-center gap-1"><Mail className="w-4 h-4" /> {s.contact_email}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BigCard({ icon: Icon, title, body, color, color2 }) {
  return (
    <div className="relative overflow-hidden bg-white rounded-3xl p-8 lg:p-10 border-2 border-slate-100 shadow-lg hover:shadow-2xl transition-all group">
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity" style={{ background: `linear-gradient(135deg, ${color}, ${color2})` }} />
      <div className="relative">
        <div className="w-14 h-14 rounded-2xl text-white flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${color}, ${color2})` }}>
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="mt-5 text-2xl font-black text-slate-900">{title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line">{body}</p>
      </div>
    </div>
  );
}
